import { useApolloClient } from "@apollo/client";
import { SubmitButton } from "Components/Button";
import { Horizontal } from "Components/Horizontal";
import { Modal } from "Components/Modal";
import {
  AdminUsersQuery,
  createOrUpdateAdminMutation,
  Role,
} from "generated/graphql";
import { Dropdown } from "Inputs/Dropdown";
import { EnterKeyCatcher } from "Inputs/EnterKeyCatcher";
import { useFormWithSchema, withFieldValueConverter } from "Inputs/FormTools";
import { VerticalRadio } from "Inputs/Radio";
import { Textbox } from "Inputs/Textbox";
import { YupDefined } from "Inputs/Tools";
import React from "react";
import { serverConstants } from "Shared/Constants";
import { hasValueFn } from "Tools";
import * as Yup from "yup";

const roleDisplayOrder = [Role.SUPER_ADMIN, Role.ADMIN];

function useComponentFormik(props: {
  userToEdit?: AdminUsersQuery["adminUsers"][number];
  onClose: () => void;
}) {
  const apolloClient = useApolloClient();
  return useFormWithSchema({
    initialValues: {
      firstName: null,
      lastName: null,
      email: null,
      isActive: true,
      role:
        hasValueFn(props.userToEdit, u =>
          roleDisplayOrder.find(r => u.roles.includes(r)),
        ) ?? Role.ADMIN,
      ...props.userToEdit,
      confirmEmail: props.userToEdit?.email ?? null,
    },
    onSubmit: async (values, handlers) => {
      const result = await apolloClient.mutate({
        mutation: createOrUpdateAdminMutation,
        variables: {
          input: {
            userId: props.userToEdit?.id,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            isActive: values.isActive,
            role: values.role,
          },
        },
      });
      const payload = result.data!.createOrUpdateAdmin;
      if (payload.emailInUseError === true) {
        handlers.setErrors({
          email: "This email address is associated with an existing account",
        });
        return;
      }
      props.onClose();
    },
    validationSchema: Yup.object({
      firstName: YupDefined.string().nullable().label("First Name").required(),
      lastName: YupDefined.string().nullable().label("Last Name").required(),
      email: YupDefined.string()
        .nullable()
        .label("Email")
        .email("Please enter a valid email.")
        .required(),
      confirmEmail: YupDefined.string()
        .nullable()
        .label("Confirm Email")
        .oneOf([Yup.ref("email")], "Email addresses must match")
        .email("Please enter a valid email.")
        .required(),
      isActive: YupDefined.boolean().label("Status"),
      role: YupDefined.enum(Role).label("Role"),
    }),
  });
}

export function AddOrEditAccountModal(props: {
  isVisible: boolean;
  userToEdit?: AdminUsersQuery["adminUsers"][number];
  onClose: () => void;
}) {
  const formik = useComponentFormik(props);
  return (
    <Modal
      title={`${props.userToEdit ? "Edit" : "Add New"} Admin Account`}
      isVisible={props.isVisible}
      buttonRow={
        <SubmitButton
          isSubmitting={formik.isSubmitting}
          onClick={formik.submitForm}
        />
      }
      onCancel={props.onClose}
    >
      <EnterKeyCatcher handleSubmit={formik.handleSubmit}>
        <Horizontal label="Name" isRequired>
          <div className="field">
            <Textbox
              fieldProps={formik.field.getSubField("firstName")}
              placeholder="First Name"
              style={{ flexGrow: 1 }}
            />
          </div>
          <div className="field">
            <Textbox
              fieldProps={formik.field.getSubField("lastName")}
              placeholder="Last Name"
              style={{ flexGrow: 1 }}
            />
          </div>
        </Horizontal>
        <Horizontal label="Email" isRequired>
          <Textbox
            type="email"
            fieldProps={formik.field.getSubField("email")}
            placeholder={`name@${serverConstants.StaticSiteHost}`}
            style={{ flexGrow: 1 }}
          />
        </Horizontal>
        <Horizontal label="Confirm Email" isRequired>
          <Textbox
            type="email"
            fieldProps={formik.field.getSubField("confirmEmail")}
            placeholder={`name@${serverConstants.StaticSiteHost}`}
            style={{ flexGrow: 1 }}
          />
        </Horizontal>
        <Horizontal label="Role" isRequired>
          <Dropdown
            field={formik.field.role}
            options={[
              [Role.ADMIN, "Admin"],
              [Role.SUPER_ADMIN, "Super Admin"],
            ]}
          />
        </Horizontal>
        {props.userToEdit && (
          <Horizontal label="Status" isRequired>
            <VerticalRadio
              fieldProps={withFieldValueConverter<boolean, string>(
                formik.field.isActive,
                {
                  format: e => e.toString(),
                  parse: e => e === "true",
                },
              )}
              options={[
                ["true", "Active"],
                ["false", "Inactive"],
              ]}
            />
          </Horizontal>
        )}
      </EnterKeyCatcher>
    </Modal>
  );
}
